import React, { useState, useEffect } from 'react'
import { useInView } from 'react-intersection-observer'
import { Quote, ChevronLeft, ChevronRight } from 'lucide-react'
import Card from './Card'
import { testimonials } from '../data/testimonials'

const Testimonials = () => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })

  const [currentPage, setCurrentPage] = useState(0)
  const cardsPerPage = 3
  const totalPages = Math.ceil(testimonials.length / cardsPerPage)

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentPage((prev) => (prev + 1) % totalPages)
    }, 5000)
    return () => clearInterval(interval)
  }, [totalPages])

  const nextPage = () => setCurrentPage((prev) => (prev + 1) % totalPages)
  const prevPage = () => setCurrentPage((prev) => (prev - 1 + totalPages) % totalPages)
  const goToPage = (page) => setCurrentPage(page)

  return (
    <section id="testimonials" ref={ref} className="py-20 px-4 sm:px-6 lg:px-8 bg-white dark:bg-navy-dark">
      <div className="max-w-7xl mx-auto">
        <div className={`transition-all duration-1000 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-navy-dark dark:text-navy-lightest mb-4">Client Testimonials</h2>
            <div className="w-20 h-1 bg-gradient-to-r from-navy-medium to-navy-deep dark:from-github-border dark:to-github-border mx-auto mb-4"></div>
            <p className="text-xl text-navy-base dark:text-navy-soft max-w-2xl mx-auto">What clients and colleagues say about working with me</p>
          </div>
          <div className="relative max-w-7xl mx-auto px-4">
            <div className="relative overflow-hidden">
              <div className="flex transition-transform duration-700 ease-in-out gap-6" style={{ transform: `translateX(-${currentPage * 100}%)` }}>
                {Array.from({ length: totalPages }).map((_, pageIndex) => (
                  <div key={pageIndex} className="min-w-full flex gap-6">
                    {testimonials.slice(pageIndex * cardsPerPage, (pageIndex + 1) * cardsPerPage).map((testimonial) => (
                      <div key={testimonial.id} className="flex-1">
                        <Card className="h-[400px] p-6 bg-white dark:bg-slate-800 hover:shadow-xl transition-all duration-300 border border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 flex flex-col">
                          <Quote className="text-slate-400 dark:text-slate-500 mb-4 flex-shrink-0" size={28} />
                          <div className="flex-1 mb-6 overflow-y-auto">
                            <p className="text-sm md:text-base text-slate-600 dark:text-slate-300 leading-relaxed">"{testimonial.quote}"</p>
                          </div>
                          <div className="pt-4 border-t border-slate-200 dark:border-slate-700 flex-shrink-0">
                            <div className="flex items-center gap-3 mb-3">
                              <img src={testimonial.avatar} alt={testimonial.name} className="w-12 h-12 rounded-full object-cover" />
                              <div className="flex-1 min-w-0">
                                <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">{testimonial.name}</h4>
                                <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{testimonial.role}</p>
                                <p className="text-xs text-slate-600 dark:text-slate-300 truncate">{testimonial.company}</p>
                              </div>
                            </div>
                            <div className="flex gap-1 justify-center">
                              {[...Array(5)].map((_, i) => (
                                <svg key={i} className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                                </svg>
                              ))}
                            </div>
                          </div>
                        </Card>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
            <button onClick={prevPage} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 w-10 h-10 bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-full shadow-lg flex items-center justify-center text-slate-700 dark:text-slate-300 transition-all duration-300 border border-slate-200 dark:border-slate-700 z-10">
              <ChevronLeft size={20} />
            </button>
            <button onClick={nextPage} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 w-10 h-10 bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-full shadow-lg flex items-center justify-center text-slate-700 dark:text-slate-300 transition-all duration-300 border border-slate-200 dark:border-slate-700 z-10">
              <ChevronRight size={20} />
            </button>
            <div className="flex justify-center gap-2 mt-8">
              {Array.from({ length: totalPages }).map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => goToPage(idx)}
                  className={`transition-all duration-300 rounded-full ${
                    idx === currentPage
                      ? 'w-8 h-2 bg-slate-700 dark:bg-slate-300'
                      : 'w-2 h-2 bg-slate-300 dark:bg-slate-600 hover:bg-slate-400 dark:hover:bg-slate-500'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default Testimonials
